import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Camera, Leaf, Music, Brain, Gift, ArrowRight, CircleDot, Moon, FlaskConical, Fingerprint, Info } from 'lucide-react';
import { Logo } from './Logo';
import { useClickSound } from '../hooks/useClickSound';
import { AuraVisualization } from './AuraVisualization';
import { getProfileAnswers } from './ritual/ProfilingQuestion';
function getTimeOfDay() {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) {
    return {
      greeting: 'Dzień dobry',
      subtitle: 'Czas na chwilę dla Ciebie.',
      ctaTitle: 'Rozpocznij swój dzień',
      ctaSubtitle: 'Skaner • Oddech • Afirmacje',
      ctaBadge: 'Rytuał Poranny',
      gradient: 'from-zen-sage to-emerald-600',
      shadow: 'shadow-zen-sage/20',
      glowColor: 'bg-white/10'
    };
  } else if (hour >= 12 && hour < 18) {
    return {
      greeting: 'Cześć',
      subtitle: 'Doładuj swoją energię.',
      ctaTitle: 'Energia na resztę dnia',
      ctaSubtitle: 'Dźwięk • Oddech • Afirmacje',
      ctaBadge: 'Rytuał Południa',
      gradient: 'from-amber-500 to-orange-600',
      shadow: 'shadow-amber-500/20',
      glowColor: 'bg-yellow-200/20'
    };
  } else {
    return {
      greeting: 'Dobry wieczór',
      subtitle: 'Czas się wyciszyć i zregenerować.',
      ctaTitle: 'Relaks i wyciszenie',
      ctaSubtitle: 'Terapia • Oddech • Medytacja',
      ctaBadge: 'Rytuał Wieczorny',
      gradient: 'from-indigo-600 to-purple-800',
      shadow: 'shadow-indigo-600/20',
      glowColor: 'bg-purple-300/10'
    };
  }
}
interface DashboardProps {
  energyPoints: number;
  onOpenEnergyPopup: () => void;
  onOpenScanner: () => void;
  onOpenBoutique: () => void;
  onOpenProfile: () => void;
  onOpenSoundCatalog: () => void;
  onOpenBeliefDetox: () => void;
  onOpenAffirmations: () => void;
  onOpenChakraCleansing: () => void;
  onOpenDeepSleep: () => void;
  onOpenSomaticTracking: () => void;
  onOpenLandingPage: () => void;
  onGoHome: () => void;
}
export function Dashboard({
  energyPoints,
  onOpenEnergyPopup,
  onOpenScanner,
  onOpenBoutique,
  onOpenProfile,
  onOpenSoundCatalog,
  onOpenBeliefDetox,
  onOpenAffirmations,
  onOpenChakraCleansing,
  onOpenDeepSleep,
  onOpenSomaticTracking,
  onOpenLandingPage,
  onGoHome
}: DashboardProps) {
  const {
    playClick
  } = useClickSound();
  const timeConfig = getTimeOfDay();
  const [showSimPanel, setShowSimPanel] = useState(false);
  const profileName = getProfileAnswers().name?.trim() || '';
  return <motion.div className="flex flex-col items-center w-full max-w-lg z-10 pb-8 mt-4 p-4" initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: -20
  }} transition={{
    duration: 0.6
  }} data-id="element-338">
      {/* Top Logo */}
      <div className="w-full flex justify-center mb-6" data-id="element-339">
        <Logo onClick={onGoHome} data-id="element-340" />
      </div>

      {/* Header */}
      <div className="w-full flex justify-between items-center mb-6 px-2" data-id="element-341">
        <div data-id="element-342">
          <h2 className="text-2xl font-light text-zen-text" data-id="element-343">
            {timeConfig.greeting},{' '}
            <span className="font-medium" data-id="element-344">{profileName}</span>
          </h2>
          <p className="text-xs text-slate-400 mt-1" data-id="element-345">{timeConfig.subtitle}</p>
        </div>
        <div className="flex items-center space-x-3" data-id="element-346">
          <button onClick={() => {
          playClick();
          onOpenEnergyPopup();
        }} className="clean-panel px-3 py-1.5 rounded-full flex items-center space-x-1 border-yellow-200 bg-yellow-50/50 hover:bg-yellow-100/50 transition-colors cursor-pointer" data-id="element-347">
            <Sparkles className="w-4 h-4 text-yellow-500 animate-pulse" fill="currentColor" data-id="element-348" />
            <span className="text-sm font-bold text-yellow-600" data-id="element-349">
              {energyPoints}
            </span>
          </button>
          <button onClick={() => {
          playClick();
          setShowSimPanel(prev => !prev);
        }} className={`w-8 h-8 rounded-full flex items-center justify-center transition-all cursor-pointer ${showSimPanel ? 'bg-indigo-100 border border-indigo-200' : 'bg-slate-100 border border-slate-200'}`} aria-label="Panel symulacji" data-id="element-350">
            <FlaskConical className={`w-3.5 h-3.5 ${showSimPanel ? 'text-indigo-500' : 'text-slate-400'}`} strokeWidth={2} data-id="element-351" />
          </button>
          <button onClick={() => {
          playClick();
          onOpenProfile();
        }} className="w-10 h-10 rounded-full bg-zen-bg border border-zen-accent/30 flex items-center justify-center shadow-sm overflow-hidden hover:ring-2 hover:ring-zen-accent/50 transition-all cursor-pointer" aria-label="Twój profil" data-id="element-352">
            <img src="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop" className="w-full h-full object-cover opacity-80" alt="Avatar" data-id="element-353" />
          </button>
        </div>
      </div>

      {/* Aura Visualization */}
      <AuraVisualization energyPoints={energyPoints} showSimPanel={showSimPanel} onOpenScanner={onOpenScanner} onOpenSoundCatalog={onOpenSoundCatalog} onOpenBeliefDetox={onOpenBeliefDetox} onOpenChakraCleansing={onOpenChakraCleansing} onOpenAffirmations={onOpenAffirmations} data-id="element-354" />

      {/* Daily Ritual Prompt */}
      <div className="w-full mb-6" data-id="element-355">
        <button onClick={() => {
        playClick();
        onOpenScanner();
      }} className={`w-full bg-gradient-to-r ${timeConfig.gradient} rounded-3xl p-5 text-left relative overflow-hidden shadow-lg ${timeConfig.shadow} group`} data-id="element-356">
          <div className={`absolute top-0 right-0 w-32 h-32 ${timeConfig.glowColor} rounded-full blur-2xl -mr-10 -mt-10 transition-transform group-hover:scale-110`} data-id="element-357"></div>
          <div className="relative z-10 flex justify-between items-center" data-id="element-358">
            <div data-id="element-359">
              <div className="inline-flex items-center space-x-1 bg-white/20 text-white text-[10px] px-2 py-0.5 rounded-full font-bold mb-2 backdrop-blur-sm border border-white/20" data-id="element-360">
                <Sparkles className="w-3 h-3" fill="currentColor" data-id="element-361" />
                <span data-id="element-362">{timeConfig.ctaBadge}</span>
              </div>
              <h3 className="text-lg font-bold text-white mb-1" data-id="element-363">
                {timeConfig.ctaTitle}
              </h3>
              <p className="text-xs text-white/80" data-id="element-364">{timeConfig.ctaSubtitle}</p>
            </div>
            <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white border border-white/20 group-hover:bg-white/30 transition-colors" data-id="element-365">
              <ArrowRight className="w-5 h-5" data-id="element-366" />
            </div>
          </div>
        </button>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-2 gap-4 w-full" data-id="element-367">
        {/* Module 1 - Skaner Emocji */}
        <button onClick={() => {
        playClick();
        onOpenScanner();
      }} className="clean-panel rounded-3xl p-5 flex flex-col items-center text-center group hover:border-zen-accent/50 transition-all relative" data-id="element-368">
          <div className="absolute top-3 right-3 bg-yellow-100 rounded-full px-2 py-0.5 text-[10px] text-yellow-700 flex items-center font-semibold" data-id="element-369">
            15 <Sparkles className="w-3 h-3 ml-1" fill="currentColor" data-id="element-370" />
          </div>
          <div className="w-14 h-14 rounded-full bg-slate-50 mb-3 flex items-center justify-center border border-slate-100 group-hover:scale-105 transition-transform shadow-sm" data-id="element-371">
            <Camera className="w-6 h-6 text-slate-600" strokeWidth={1.5} data-id="element-372" />
          </div>
          <h3 className="font-semibold text-sm mb-1 text-zen-text" data-id="element-373">
            Skaner Emocji
          </h3>
          <p className="text-[10px] text-slate-500" data-id="element-374">
            Analiza nastroju, energii i aury
          </p>
        </button>

        {/* Module 2 */}
        <button onClick={() => {
        playClick();
        onOpenAffirmations();
      }} className="clean-panel rounded-3xl p-5 flex flex-col items-center text-center group hover:border-zen-sage/50 transition-all relative" data-id="element-375">
          <div className="absolute top-3 right-3 bg-green-50 rounded-full px-2 py-0.5 text-[10px] text-green-600 flex items-center font-semibold border border-green-100" data-id="element-376">
            Free
          </div>
          <div className="w-14 h-14 rounded-full bg-zen-sage/10 mb-3 flex items-center justify-center border border-zen-sage/20 group-hover:scale-105 transition-transform" data-id="element-377">
            <Leaf className="w-6 h-6 text-zen-sage" strokeWidth={1.5} data-id="element-378" />
          </div>
          <h3 className="font-semibold text-sm mb-1 text-zen-text" data-id="element-379">
            Afirmacje
          </h3>
          <p className="text-[10px] text-slate-500" data-id="element-380">Mantry i pozytywne kody</p>
        </button>

        {/* Module 3 */}
        <button onClick={() => {
        playClick();
        onOpenSoundCatalog();
      }} className="clean-panel rounded-3xl p-5 flex flex-col items-center text-center group hover:border-zen-accent/50 transition-all relative overflow-hidden" data-id="element-381">
          <div className="absolute inset-0 bg-zen-accent/5 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-382"></div>
          <div className="absolute top-3 right-3 bg-green-50 rounded-full px-2 py-0.5 text-[10px] text-green-600 flex items-center font-semibold border border-green-100" data-id="element-383">
            Free
          </div>
          <div className="w-14 h-14 rounded-full bg-zen-accent/10 mb-3 flex items-center justify-center border border-zen-accent/20 group-hover:scale-105 transition-transform" data-id="element-384">
            <Music className="w-6 h-6 text-zen-accent animate-pulse-slow" strokeWidth={1.5} data-id="element-385" />
          </div>
          <h3 className="font-semibold text-sm mb-1 text-zen-text" data-id="element-386">
            Balans Dźwiękiem
          </h3>
          <p className="text-[10px] text-slate-500" data-id="element-387">
            Terapia częstotliwościami
          </p>
        </button>

        {/* Module 4 */}
        <button onClick={() => {
        playClick();
        onOpenBeliefDetox();
      }} className="clean-panel rounded-3xl p-5 flex flex-col items-center text-center group hover:border-blue-200 transition-all relative" data-id="element-388">
          <div className="absolute top-3 right-3 bg-yellow-100 rounded-full px-2 py-0.5 text-[10px] text-yellow-700 flex items-center font-semibold" data-id="element-389">
            30 <Sparkles className="w-3 h-3 ml-1" fill="currentColor" data-id="element-390" />
          </div>
          <div className="w-14 h-14 rounded-full bg-blue-50 mb-3 flex items-center justify-center border border-blue-100 group-hover:scale-105 transition-transform" data-id="element-391">
            <Brain className="w-6 h-6 text-blue-500" strokeWidth={1.5} data-id="element-392" />
          </div>
          <h3 className="font-semibold text-sm mb-1 text-zen-text" data-id="element-393">
            Detoks Przekonań
          </h3>
          <p className="text-[10px] text-slate-500" data-id="element-394">Przeprogramowanie Theta</p>
        </button>

        {/* Module 5 - Chakra Cleansing */}
        <button onClick={() => {
        playClick();
        onOpenChakraCleansing();
      }} className="clean-panel rounded-3xl p-5 flex flex-col items-center text-center group hover:border-rose-200 transition-all relative" data-id="element-395">
          <div className="absolute top-3 right-3 bg-green-50 rounded-full px-2 py-0.5 text-[10px] text-green-600 flex items-center font-semibold border border-green-100" data-id="element-396">
            Free
          </div>
          <div className="w-14 h-14 rounded-full bg-rose-50 mb-3 flex items-center justify-center border border-rose-100 group-hover:scale-105 transition-transform" data-id="element-397">
            <CircleDot className="w-6 h-6 text-rose-500" strokeWidth={1.5} data-id="element-398" />
          </div>
          <h3 className="font-semibold text-sm mb-1 text-zen-text" data-id="element-399">
            Oczyszczanie Czakr
          </h3>
          <p className="text-[10px] text-slate-500" data-id="element-400">Oddech pudełkowy</p>
        </button>

        {/* Module 6 - Deep Sleep */}
        <button onClick={() => {
        playClick();
        onOpenDeepSleep();
      }} className="clean-panel rounded-3xl p-5 flex flex-col items-center text-center group hover:border-indigo-200 transition-all relative" data-id="element-401">
          <div className="absolute top-3 right-3 bg-green-50 rounded-full px-2 py-0.5 text-[10px] text-green-600 flex items-center font-semibold border border-green-100" data-id="element-402">
            Free
          </div>
          <div className="w-14 h-14 rounded-full bg-indigo-50 mb-3 flex items-center justify-center border border-indigo-100 group-hover:scale-105 transition-transform" data-id="element-403">
            <Moon className="w-6 h-6 text-indigo-500" strokeWidth={1.5} data-id="element-404" />
          </div>
          <h3 className="font-semibold text-sm mb-1 text-zen-text" data-id="element-405">
            Głęboki Sen
          </h3>
          <p className="text-[10px] text-slate-500" data-id="element-406">Fale Delta na wieczór</p>
        </button>

        {/* Module 7 - Somatic Tracking */}
      </div>

      {/* Referral */}
      <button onClick={() => {
      playClick();
      const shareUrl = 'https://www.google.com/search?q=terapia+kamertonami&oq=terapia+kamertonami';
      const shareData = {
        title: 'Dobrostan — Terapia Dźwiękiem',
        text: 'Hej! Odkryłam niesamowitą aplikację do terapii dźwiękiem i medytacji. Dołącz do mnie! 🧘‍♀️✨',
        url: shareUrl
      };
      if (navigator.share) {
        navigator.share(shareData).catch(() => {});
      } else {
        window.open(shareUrl, '_blank');
      }
    }} className="w-full mt-6 clean-panel rounded-2xl p-4 flex items-center justify-between hover:bg-slate-50 transition-colors" data-id="element-407">
        <div className="flex items-center space-x-4" data-id="element-408">
          <div className="w-10 h-10 rounded-full bg-peach-50 flex items-center justify-center" data-id="element-409">
            <Gift className="w-5 h-5 text-orange-400" strokeWidth={1.5} data-id="element-410" />
          </div>
          <div className="text-left" data-id="element-411">
            <span className="block text-sm text-zen-text font-semibold" data-id="element-412">
              Podaruj Dobrostan
            </span>
            <span className="block text-[11px] text-slate-500 mt-0.5" data-id="element-413">
              Zaproś przyjaciółkę i zyskaj energię
            </span>
          </div>
        </div>
        <div className="bg-yellow-50 text-yellow-600 text-xs px-3 py-1.5 rounded-full font-bold flex items-center border border-yellow-100" data-id="element-414">
          +50 <Sparkles className="w-3 h-3 ml-1" fill="currentColor" data-id="element-415" />
        </div>
      </button>

      {/* Shop */}
      <div className="w-full mt-10" data-id="element-416">
        <div className="flex items-center justify-between mb-4 px-1" data-id="element-417">
          <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-widest" data-id="element-418">
            Wsparcie Terapii
          </h3>
          <button onClick={() => {
          playClick();
          onOpenBoutique();
        }} className="text-[10px] text-zen-accent font-medium cursor-pointer hover:underline" data-id="element-419">
            Odkryj Butik &rarr;
          </button>
        </div>

        <div className="grid grid-cols-2 gap-4" data-id="element-420">
          {/* Product 1 */}
          <div className="clean-panel rounded-2xl p-4 flex flex-col items-center relative overflow-hidden group" data-id="element-421">
            <div className="absolute top-0 right-0 bg-yellow-100 text-yellow-700 text-[10px] px-2 py-1 rounded-bl-xl font-bold flex items-center z-10" data-id="element-422">
              +500 <Sparkles className="w-3 h-3 ml-1" fill="currentColor" data-id="element-423" />
            </div>
            <div className="w-24 h-24 rounded-full overflow-hidden mb-4 border border-slate-100 shadow-sm" data-id="element-424">
              <img src="https://images.unsplash.com/photo-1599305090598-fe179d501227?w=200&h=200&fit=crop" className="w-full h-full object-cover transition-transform group-hover:scale-110 duration-500" alt="Zestaw Aromaterapii" data-id="element-425" />
            </div>
            <h4 className="text-sm font-semibold text-zen-text text-center mb-1" data-id="element-426">
              Zestaw Aromaterapii
            </h4>
            <p className="text-[10px] text-slate-500 text-center mb-4 h-6" data-id="element-427">
              Wyciszenie przed snem
            </p>
            <button onClick={() => {
            playClick();
            onOpenBoutique();
          }} className="w-full bg-zen-bg hover:bg-slate-50 border border-slate-200 rounded-xl py-2.5 text-xs text-zen-text font-medium transition-colors flex justify-center items-center" data-id="element-428">
              Sprawdź
            </button>
          </div>

          {/* Product 2 */}
          <div className="clean-panel rounded-2xl p-4 flex flex-col items-center relative overflow-hidden group" data-id="element-429">
            <div className="absolute top-0 right-0 bg-yellow-100 text-yellow-700 text-[10px] px-2 py-1 rounded-bl-xl font-bold flex items-center z-10" data-id="element-430">
              +1200 <Sparkles className="w-3 h-3 ml-1" fill="currentColor" data-id="element-431" />
            </div>
            <div className="w-24 h-24 rounded-full overflow-hidden mb-4 border border-slate-100 shadow-sm" data-id="element-432">
              <img src="https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=200&h=200&fit=crop" className="w-full h-full object-cover transition-transform group-hover:scale-110 duration-500" alt="Mata Pranamat" data-id="element-433" />
            </div>
            <h4 className="text-sm font-semibold text-zen-text text-center mb-1" data-id="element-434">
              Mata Pranamat
            </h4>
            <p className="text-[10px] text-slate-500 text-center mb-4 h-6" data-id="element-435">
              Głęboki relaks ciała
            </p>
            <button onClick={() => {
            playClick();
            onOpenBoutique();
          }} className="w-full bg-zen-bg hover:bg-slate-50 border border-slate-200 rounded-xl py-2.5 text-xs text-zen-text font-medium transition-colors flex justify-center items-center" data-id="element-436">
              Sprawdź
            </button>
          </div>
        </div>
      </div>

      {/* Somatic Tracking Tile */}
      <button onClick={() => {
      playClick();
      onOpenSomaticTracking();
    }} className="w-full mt-6 clean-panel rounded-2xl p-4 flex items-center justify-between hover:bg-slate-50 transition-colors group border border-pink-100" data-id="element-437">
        <div className="flex items-center space-x-4" data-id="element-438">
          <div className="w-10 h-10 rounded-full bg-pink-50 flex items-center justify-center group-hover:scale-105 transition-transform" data-id="element-439">
            <Fingerprint className="w-5 h-5 text-pink-500" strokeWidth={1.5} data-id="element-440" />
          </div>
          <div className="text-left" data-id="element-441">
            <span className="block text-sm text-zen-text font-semibold" data-id="element-442">
              Śledzenie Somatyczne
            </span>
            <span className="block text-[11px] text-slate-500 mt-0.5" data-id="element-443">
              6 ćwiczeń regulujących układ nerwowy
            </span>
          </div>
        </div>
        <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-pink-500 transition-colors" data-id="element-444" />
      </button>

      {/* Footer Link */}
      <button onClick={() => {
      playClick();
      onOpenLandingPage();
    }} className="w-full mt-8 clean-panel rounded-2xl p-4 flex items-center justify-center hover:bg-slate-50 transition-colors text-xs text-slate-400 group" data-id="element-445">
        <Info className="w-4 h-4 text-slate-400 group-hover:text-zen-accent transition-colors" data-id="element-446" />
        <span className="ml-2" data-id="element-447">O aplikacji</span>
      </button>
    </motion.div>;
}